import React, { useState } from 'react';
import { StandingCell, StandingRow, StandingTeam } from '../utils/standings';

interface StandingsMatrixProps {
  columns: StandingTeam[];
  rows: StandingRow[];
}

const cellBorder = '1px solid #dbe4f0';
const TEAM_COL_WIDTH = 56;
const SUMMARY_COL_WIDTH = 56;
const LOGO_SIZE = 32;

function getTeamLogoUrl(teamId: string): string {
  return `https://pub-e0ccb659989a49a297dc22911f822b08.r2.dev/photos/teams/logos/${teamId}.png`;
}

function TeamLogo({ team, size = LOGO_SIZE }: { team: StandingTeam; size?: number }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div
        title={team.name}
        aria-label={team.name}
        style={{
          width: size,
          height: size,
          borderRadius: 8,
          background: '#1976d2',
          color: 'white',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: size * 0.45,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {team.name.charAt(0)}
      </div>
    );
  }

  return (
    <img
      src={getTeamLogoUrl(team.id)}
      alt={team.name}
      title={team.name}
      style={{
        width: size,
        height: size,
        objectFit: 'contain',
        borderRadius: 8,
        backgroundColor: '#ffffff',
        flexShrink: 0,
      }}
      onError={() => setFailed(true)}
    />
  );
}

const summaryHeaderStyle: React.CSSProperties = {
  padding: '10px 4px',
  fontSize: 12,
  fontWeight: 700,
  color: '#2d3748',
  backgroundColor: '#f8fafc',
  borderBottom: cellBorder,
  borderRight: cellBorder,
  textAlign: 'center',
  whiteSpace: 'nowrap',
  width: SUMMARY_COL_WIDTH,
};

function ResultCell({ cell }: { cell: StandingCell }) {
  const won = cell.scoreFor > cell.scoreAgainst;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        minHeight: 52,
        padding: '6px 2px',
        width: '100%',
      }}
    >
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: '#2d3748',
          letterSpacing: 0.2,
          whiteSpace: 'nowrap',
        }}
      >
        {cell.scoreFor} : {cell.scoreAgainst}
      </span>
      <span
        style={{
          minWidth: 22,
          height: 22,
          borderRadius: 999,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 700,
          color: won ? '#1a4d8c' : '#4a5568',
          backgroundColor: won ? '#dbeafe' : '#edf2f7',
        }}
      >
        {cell.points}
      </span>
    </div>
  );
}

const StandingsMatrix: React.FC<StandingsMatrixProps> = ({ columns, rows }) => {
  if (columns.length === 0) {
    return (
      <div
        style={{
          padding: '24px',
          textAlign: 'center',
          color: '#718096',
          fontSize: 14,
        }}
      >
        暫無積分資料
      </div>
    );
  }

  // Keep a usable touch-scroll floor on phones; stretch to 100% on wider screens.
  const minTableWidth =
    TEAM_COL_WIDTH + columns.length * 72 + SUMMARY_COL_WIDTH * 4;

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        backgroundColor: '#ffffff',
        borderRadius: 16,
        border: '1px solid #e2e8f0',
        boxShadow: '0 8px 24px rgba(49, 130, 206, 0.08)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '14px 18px',
          borderBottom: '1px solid #e2e8f0',
          background: 'linear-gradient(180deg, #f7fbff 0%, #eef6ff 100%)',
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: 18,
            fontWeight: 700,
            color: '#1a365d',
          }}
        >
          積分榜
        </h3>
      </div>

      <div
        style={{
          width: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <table
          style={{
            display: 'table',
            borderCollapse: 'separate',
            borderSpacing: 0,
            width: '100%',
            minWidth: `max(100%, ${minTableWidth}px)`,
            tableLayout: 'fixed',
          }}
        >
          <colgroup>
            <col style={{ width: TEAM_COL_WIDTH }} />
            {columns.map((team) => (
              <col key={team.id} />
            ))}
            <col style={{ width: SUMMARY_COL_WIDTH }} />
            <col style={{ width: SUMMARY_COL_WIDTH }} />
            <col style={{ width: SUMMARY_COL_WIDTH }} />
            <col style={{ width: SUMMARY_COL_WIDTH }} />
          </colgroup>
          <thead>
            <tr>
              <th
                style={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  backgroundColor: '#f8fafc',
                  borderBottom: cellBorder,
                  borderRight: cellBorder,
                  boxShadow: '4px 0 8px -2px rgba(0,0,0,0.05)',
                  clipPath: 'inset(0 -15px -1px 0)', // prevent shadow from leaking top/bottom
                }}
              />
              {columns.map((team) => (
                <th
                  key={team.id}
                  style={{
                    padding: '8px 4px',
                    backgroundColor: '#f8fafc',
                    borderBottom: cellBorder,
                    borderRight: cellBorder,
                    textAlign: 'center',
                    verticalAlign: 'middle',
                  }}
                  title={team.name}
                >
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <TeamLogo team={team} />
                  </div>
                </th>
              ))}
              <th style={summaryHeaderStyle}>積分</th>
              <th style={summaryHeaderStyle}>得分</th>
              <th style={summaryHeaderStyle}>失分</th>
              <th
                style={{
                  ...summaryHeaderStyle,
                  borderRight: 'none',
                }}
              >
                名次
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.team.id}>
                <th
                  scope="row"
                  style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    padding: '8px 4px',
                    backgroundColor: '#f8fafc',
                    borderBottom: cellBorder,
                    borderRight: cellBorder,
                    textAlign: 'center',
                    verticalAlign: 'middle',
                    boxShadow: '4px 0 8px -2px rgba(0,0,0,0.05)',
                    clipPath: 'inset(0 -15px -1px 0)',
                  }}
                  title={row.team.name}
                >
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <TeamLogo team={row.team} />
                  </div>
                </th>
                {row.cells.map((cell, colIndex) => (
                  <td
                    key={`${row.team.id}-${columns[colIndex].id}`}
                    style={{
                      borderBottom: cellBorder,
                      borderRight: cellBorder,
                      backgroundColor: cell === 'self' ? '#edf2f7' : '#ffffff',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                    }}
                  >
                    {cell === 'self' ? (
                      <span
                        style={{
                          color: '#a0aec0',
                          fontWeight: 700,
                          fontSize: 16,
                        }}
                      >
                        ×
                      </span>
                    ) : cell ? (
                      <ResultCell cell={cell} />
                    ) : (
                      <span style={{ color: '#cbd5e0' }}>—</span>
                    )}
                  </td>
                ))}
                <td
                  style={{
                    borderBottom: cellBorder,
                    borderRight: cellBorder,
                    backgroundColor: '#ffffff',
                    textAlign: 'center',
                    fontWeight: 800,
                    fontSize: 15,
                    color: '#1a365d',
                  }}
                >
                  {row.points}
                </td>
                <td
                  style={{
                    borderBottom: cellBorder,
                    borderRight: cellBorder,
                    backgroundColor: '#ffffff',
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: 14,
                    color: '#2d3748',
                  }}
                >
                  {row.pointsScored}
                </td>
                <td
                  style={{
                    borderBottom: cellBorder,
                    borderRight: cellBorder,
                    backgroundColor: '#ffffff',
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: 14,
                    color: '#2d3748',
                  }}
                >
                  {row.pointsAgainst}
                </td>
                <td
                  style={{
                    borderBottom: cellBorder,
                    backgroundColor: '#ffffff',
                    textAlign: 'center',
                    fontWeight: 800,
                    fontSize: 15,
                    color: '#2d3748',
                  }}
                >
                  {row.rank}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StandingsMatrix;
